import React, { useState, useEffect } from 'react';
import PageTabs from '../shared/PageTabs';
import { USERS_TABS } from '../shared/tabConfigs';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Chip,
    TablePagination,
    TextField,
    InputAdornment,
    CircularProgress,
    Menu, ListItemIcon, ListItemText, MenuItem
} from '@mui/material';
import { Search, DeleteForever, Warning, RestoreFromTrash } from '@mui/icons-material';
import { userService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateFormatter';
import EmptyState from '../common/EmptyState';
const PurgeUsers = () => {
    const [deletedUsers, setDeletedUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const { showToast } = useToast();
    const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);
    const isActionsMenuOpen = Boolean(actionsMenuAnchorEl);
    const [restoreConfirmDialog, setRestoreConfirmDialog] = useState(false);


    useEffect(() => {
        fetchDeletedUsers();
    }, [page, rowsPerPage, searchTerm]);

    const fetchDeletedUsers = async () => {
        setLoading(true);
        try {
            const response = await userService.getDeletedUsers(
                page * rowsPerPage,
                rowsPerPage,
                searchTerm
            );
            if (response.data && response.data.success) {
                setDeletedUsers(response.data.data.users || []);
                setTotalCount(response.data.data.total || 0);
            }
            else {
                setDeletedUsers([]);
                setTotalCount(0);
                showToast('Failed to fetch deleted users', 'error');
            }
        } catch (error) {
            console.error('Error fetching deleted users:', error);
            setDeletedUsers([]);
            setTotalCount(0);
            showToast('Error fetching deleted users', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked && deletedUsers && deletedUsers.length > 0) {
            setSelectedUsers(deletedUsers.map(user => user.id));
        } else {
            setSelectedUsers([]);
        }
    };

    const handleSelectUser = (userId) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    const handlePurgeUsers = async () => {
        if (selectedUsers.length === 0) {
            showToast('Please select users to purge', 'warning');
            return;
        }
        setConfirmDialog(true);
    };

    const confirmPurge = async () => {
        setLoading(true);
        try {
            const response = await userService.purgeUsers(selectedUsers);
            if (response.data && response.data.success) {
                showToast(`Successfully purged ${selectedUsers.length} users`, 'success');
                setSelectedUsers([]);
                setConfirmDialog(false);
                fetchDeletedUsers();
            }
            else {
                showToast(response.data?.message || 'Error purging users', 'error');
            }
        } catch (error) {
            console.error('Error purging users:', error);
            showToast(error.response?.data?.message || 'Error purging users', 'error');
        } finally {
            setLoading(false);
        }
    };
    const confirmRestore = async () => {
        setLoading(true);
        try {
            const response = await userService.restoreUsers(selectedUsers);
            if (response.data?.success) {
                showToast(`Successfully restored ${selectedUsers.length} users`, 'success');
                setSelectedUsers([]);
                setRestoreConfirmDialog(false);
                fetchDeletedUsers();
            } else {
                showToast(response.data?.message || 'Error restoring users', 'error');
            }
        } catch (error) {
            console.error('Error restoring users:', error);
            showToast(error.response?.data?.message || 'Error restoring users', 'error');
        } finally {
            setLoading(false);
        }
    };


    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        //return new Date(timestamp * 1000).toLocaleDateString();
        return formatDate(new Date(timestamp * 1000).toISOString());
    };
    const handleRestoreUsers = () => {
        if (selectedUsers.length === 0) {
            showToast('Please select users to restore', 'warning');
            return;
        }

        // All users in deletedUsers are already soft-deleted
        const validSelectedIds = deletedUsers
            .filter(user => selectedUsers.includes(user.id))
            .map(user => user.id);

        if (validSelectedIds.length === 0) {
            showToast('Selected users are not eligible for restore', 'warning');
            return;
        }

        // Optional: warn if some were skipped
        if (validSelectedIds.length < selectedUsers.length) {
            showToast('Some selected users were not deleted and skipped', 'warning');
        }

        setSelectedUsers(validSelectedIds);
        setRestoreConfirmDialog(true);
    };


    const handleActionsMenuClick = (event) => {
        setActionsMenuAnchorEl(event.currentTarget);
    };
    const handleActionsMenuClose = () => {
        setActionsMenuAnchorEl(null);
    };


    const safeDeletedUsers = Array.isArray(deletedUsers) ? deletedUsers : [];

    return (
        <Box sx={{ p: 3 }}>
            <PageTabs tabs={USERS_TABS} />
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" component="h1">
                    Purge Users
                </Typography>
            </Box>

            <Alert severity="warning" sx={{ mb: 3, color: '#ffffff'}}>
                <Typography variant="body2" sx={{ color: '#ffffff'}}>
                    <strong>Warning:</strong> Purging users will permanently delete them from the database along with all their associated data. This action cannot be undone.
                </Typography>
            </Alert>

            <Box
                sx={{ mb: 3,display: 'flex', justifyContent: 'space-between',  alignItems: 'center',
                        gap: 2,}}
            >
                {/* Left: Search Field */}
                <TextField
                    placeholder="Deleted Users"
                    // className="custom-search-field"
                    className="pink-filter"
                    sx={{height: '35px'}}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="end">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                />
                {/* Right: Actions Button */}
                {selectedUsers.length > 0 && (
                    <Button
                        variant="outlined"
                        onClick={handleActionsMenuClick}
                        aria-controls={isActionsMenuOpen ? 'actions-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={isActionsMenuOpen ? 'true' : undefined}
                    >
                        Actions
                    </Button>
                )}
            </Box>
            <Menu
                id="actions-menu"
                anchorEl={actionsMenuAnchorEl}
                open={isActionsMenuOpen}
                onClose={handleActionsMenuClose}
                MenuListProps={{
                    'aria-labelledby': 'actions-button',
                }}
            >
                <MenuItem onClick={() => {
                    handlePurgeUsers();
                    handleActionsMenuClose();
                }}>
                    <ListItemIcon>
                        <DeleteForever fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Purge Selected</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleRestoreUsers();
                        handleActionsMenuClose();
                    }}
                >
                    <ListItemIcon>
                        <RestoreFromTrash fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Restore Selected</ListItemText>
                </MenuItem>
            </Menu>


            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selectedUsers.length > 0 && selectedUsers.length < safeDeletedUsers.length}
                                            checked={safeDeletedUsers.length > 0 && selectedUsers.length === safeDeletedUsers.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Username</TableCell>
                                    <TableCell>Roles</TableCell>
                                    <TableCell>Created Date</TableCell>
                                    <TableCell>Deleted Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {safeDeletedUsers.map((user) => (
                                    <TableRow key={user.id}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedUsers.includes(user.id)}
                                                onChange={() => handleSelectUser(user.id)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                        <Box
                                            sx={{
                                                cursor: "pointer",
                                                color: "primary.main",
                                                }}>
                                            {user.first_name} {user.last_name}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.username}</TableCell>
                                        <TableCell>
                                            {user.role && user.role.split('|').map((role, index) => (
                                                <Chip
                                                    key={index}
                                                    label={role}
                                                    size="small"
                                                    sx={{ mr: 0.5, mb: 0.5 }}
                                                />
                                            ))}
                                        </TableCell>
                                        <TableCell>{formatDate(user.created_on)}</TableCell>
                                        <TableCell>{formatDate(user.deleted_at)}</TableCell>
                                    </TableRow>
                                ))}
                                {safeDeletedUsers.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review): a plain line. */}
                                            <EmptyState framed={false} title="No deleted users found" message={null} />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            )}            

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning color="error" />
                    Confirm Purge Users
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to permanently purge {selectedUsers.length} selected user(s)?
                        This will delete all their data including:
                    </Typography>
                    <Box component="ul" sx={{ mt: 2 }}>
                        <li>User account and profile information</li>
                        <li>Course enrollments and progress</li>
                        <li>Learning path enrollments and progress</li>
                        {/* <li>Quiz and assignment submissions</li> */}
                        <li>All associated learning data</li>
                    </Box>
                    <Alert severity="error" sx={{ mt: 2 }}>
                        This action cannot be undone!
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmPurge}
                        color="error"
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={20} /> : 'Purge Users'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={restoreConfirmDialog} onClose={() => setRestoreConfirmDialog(false)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RestoreFromTrash color="primary" />
                    Confirm Restore Users
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to restore {selectedUsers.length} selected user(s)?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRestoreConfirmDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmRestore}
                        color="primary"
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={20} /> : 'Restore Selected'}
                    </Button>
                </DialogActions>
            </Dialog>


        </Box>
    );
};

export default PurgeUsers;